import { useCallback, useEffect, useRef, useState } from 'react'

import * as Sentry from '@sentry/nextjs'

import { useLastIncidentWithClipByDeviceQuery } from '@/graphql/generated/hooks'

interface UseLastClipPreviewIProps {
  deviceId: string | undefined
  enabled: boolean
}

export const useLastClipPreview = ({
  deviceId,
  enabled,
}: UseLastClipPreviewIProps) => {
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null)
  // How long, in ms, the full preview lookup took (from kicking off the request
  // to having a still ready to render).
  const [previewLoadDurationMs, setPreviewLoadDurationMs] = useState<
    number | null
  >(null)
  const hasCapturedRef = useRef(false)
  const startTimeRef = useRef<number | null>(null)

  useEffect(() => {
    setPreviewImageUrl(null)
    setPreviewLoadDurationMs(null)
    hasCapturedRef.current = false
    startTimeRef.current = null
  }, [deviceId])

  // Start the clock as soon as the preview lookup is kicked off.
  useEffect(() => {
    if (enabled && deviceId && startTimeRef.current === null) {
      startTimeRef.current = Date.now()
    }
  }, [enabled, deviceId])

  const { data } = useLastIncidentWithClipByDeviceQuery({
    variables: { deviceId },
    skip: !enabled || !deviceId,
  })

  const sourceNode = data?.incidents?.edges?.[0]?.node
  const clipUrl = sourceNode?.video?.url ?? null

  // How old the source clip is relative to now, in seconds. Used to gauge how
  // stale the blurred preview still is (e.g. a clip pulled 45 min ago = 2700).
  const clipCreatedAt = sourceNode?.createdAt ?? null
  const previewClipAgeSeconds = clipCreatedAt
    ? Math.max(
        0,
        Math.round((Date.now() - new Date(clipCreatedAt).getTime()) / 1000)
      )
    : null

  const handleLoadedData = useCallback(
    (e: React.SyntheticEvent<HTMLVideoElement>) => {
      if (hasCapturedRef.current) return
      const video = e.currentTarget
      if (!video.videoWidth || !video.videoHeight) return

      try {
        const canvas = document.createElement('canvas')
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        const ctx = canvas.getContext('2d')
        if (!ctx) return

        ctx.drawImage(video, 0, 0)
        const dataUrl = canvas.toDataURL('image/png')
        hasCapturedRef.current = true
        setPreviewImageUrl(dataUrl)
        if (startTimeRef.current != null) {
          setPreviewLoadDurationMs(Date.now() - startTimeRef.current)
        }
      } catch (error) {
        // Canvas is likely tainted (CORS headers absent on the clip URL), which
        // blocks the still capture. Skip the preview but report so we can track
        // how often this happens.
        Sentry.captureException(error, {
          tags: {
            feature: 'clip_loader',
            surface: 'incident_drawer_blurred_preview',
          },
          extra: {
            deviceId,
          },
        })
      }
    },
    [deviceId]
  )

  const HiddenPlayer = useCallback(
    () =>
      clipUrl ? (
        <video
          crossOrigin='anonymous'
          muted
          onLoadedData={handleLoadedData}
          preload='auto'
          src={clipUrl}
          style={{ display: 'none' }}
        />
      ) : null,
    [clipUrl, handleLoadedData]
  )

  return {
    previewImageUrl,
    HiddenPlayer,
    previewClipAgeSeconds,
    previewLoadDurationMs,
  }
}
